import React, { useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateTask } from "../../../services/api/task";

import dayjs from "dayjs";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import { draftWorkOrder } from "../../../services/api/workOrder";

const UnpublishModal = ({
  unpublishModalOpen,
  setUnpublishModalOpen,
  workOrderId,
  refetchUserAllowedAction,
  workOrderStatus,
  pendingOfferCount = 0,
  isCustomer = false,
}) => {
  // A ticket awaiting client review was never on the marketplace, so reverting it
  // costs nothing. A published one is live, and any offers already received stop
  // being actionable — worth saying out loud before the click.
  const isPublished = workOrderStatus === "PUBLISHED";
  // Wording is a separate question from that. "Unpublish" is marketplace
  // language; a customer's board goes as far as "Accepted" and never mentions a
  // marketplace, so for them the action reads the same from Accepted as it does
  // from Submitted. The offer warning below still keys off the real status.
  const useMarketplaceWording = isPublished && !isCustomer;
  const dispatch = useDispatch();
  const { updateWorkOrder } = useUpdateWorkOrder();
  const {
    isPending: isMoveDraftLoading,
    error: isMoveDraftError,
    mutateAsync: moveDraftWorkOrderMutation,
  } = useMutation({
    mutationFn: draftWorkOrder,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: useMarketplaceWording
            ? "Work Order Unpublished Successfully"
            : "Work Order Moved to Draft",
          type: "success",
        })
      );
    },
  });

  async function handleMoveToDraftButton() {
    try {
      await moveDraftWorkOrderMutation(workOrderId);
      updateWorkOrder();
      refetchUserAllowedAction();
      setUnpublishModalOpen(false);
    } catch (error) {
      // Read from the thrown error, not the hook's state — isMoveDraftError has
      // not been committed yet at this point, so it toasted an empty message.
      dispatch(
        showToast({
          message:
            error?.response?.data?.message || error?.message || "Failed to move work order to draft",
          type: "error",
        })
      );
    }
  }
  return (
    <BaseModal
      className="-top-72"
      open={unpublishModalOpen}
      close={() => {
        setUnpublishModalOpen(false);
      }}
    >
      <div className="container">
        <h6 className="text-dark border-bottom mb-4 pb-1">
          {useMarketplaceWording
            ? "Unpublish this work order from the marketplace?"
            : "Move this work order to draft?"}
        </h6>
        <p>
          {useMarketplaceWording
            ? "It will be removed from the marketplace and moved to Draft. You can edit it and publish again later."
            : isPublished
              ? // Accepted, seen by the customer: it is live with engineers, but
                // saying so without the marketplace framing they never see.
                "It will stop being available to engineers and move to Draft, so you can keep editing it and submit again later."
              : "It will stop waiting for approval and move to Draft, so you can keep editing it and submit again later."}
        </p>
        {isPublished && pendingOfferCount > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <i className="fa-solid fa-triangle-exclamation mt-[3px] text-[12px] text-amber-500" />
            <p className="text-xs text-amber-800">
              {pendingOfferCount === 1
                ? "1 engineer has already responded to this work order. Their offer"
                : `${pendingOfferCount} engineers have already responded to this work order. Their offers`}{" "}
              will no longer be actionable once it leaves the marketplace.
            </p>
          </div>
        )}
      </div>
      <div
        className="flex items-center justify-center bg-white py-6 mt-3"
        style={{
          boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="py-[5px] px-2 bg-[#E8E8E8] text-dark"
          fullWidth={false}
          onClick={() => setUnpublishModalOpen(false)}
        >
          <p>Cancel</p>
        </BaseButton>
        <BaseButton
          disabled={isMoveDraftLoading}
          isLoading={isMoveDraftLoading}
          className="py-[5px] px-4 ml-4"
          color="primary"
          fullWidth={false}
          onClick={handleMoveToDraftButton}
        >
          {useMarketplaceWording ? "Unpublish" : "Move to Draft"}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default UnpublishModal;
